import { useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import { kebabCase } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useRef } from 'react';

import { RegisterOrigin } from '@pflegia/types/api/candidate/register-origin';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { ReactComponent as ArrowVertical } from '@pflegia/ui/icons/arrow-vertical.svg';
import { getButtonClassName } from '@pflegia/ui/shared/button/Button.component';
import { PostalInputProps, PostalInputVariant } from '@pflegia/ui/shared/form/postal-input/PostalInputTypes';
import { GET_JOB_TITLES, GetJobTitlesData } from '@pflegia/ui/shared/graphql/job-title';
import useJobTitle from '@pflegia/ui/shared/job-title/hooks/use-job-title';
import { removeCountryName } from '@pflegia/ui/shared/utils/remove-country-name';
import { buildLocationObject } from '@pflegia/util/common/location';

import { Skeleton, SkeletonRow } from '@components/skeleton';

import { JOB_BOARD, JOBS } from '@shared/application/constants';

import Headline from '../headline/Headline';

const PostalInputComponent = dynamic(() => import('@pflegia/ui/shared/form/postal-input/PostalInput'), {
  loading: () => (
    <Skeleton className='h-[62px] rounded-4xl bg-gray-200'>
      <SkeletonRow />
    </Skeleton>
  ),
  ssr: false,
});

const COLLAPSED_LENGTH = 15;

interface JobSearchProps {
  className?: string;
  registerOrigin?: RegisterOrigin;
}

const JobSearch: React.FC<JobSearchProps> = ({ className, registerOrigin }) => {
  const { t } = useTranslation('landingPage', { keyPrefix: 'jobSearch' });
  const { translateJobTitle } = useJobTitle();
  const { data } = useQuery<GetJobTitlesData>(GET_JOB_TITLES, {
    variables: {
      sortField: 'order',
      sortOrder: 'ASC',
    },
  });
  const scrollRef = useRef<HTMLDivElement>(null);

  const [addressInput, setAddressInput] = React.useState('');
  const [location, setLocation] = React.useState<LocationObject | null>(null);
  const handleScroll = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const handleAddressSelect = React.useCallback<NonNullable<PostalInputProps['onSelected']>>(address => {
    const locationObject = buildLocationObject(address);
    setLocation(locationObject);
    setAddressInput(removeCountryName(locationObject.formattedAddress));
    handleScroll();
  }, []);

  const [isExpanded, setIsExpanded] = React.useState(false);
  const handleExpansionClick = React.useCallback(() => {
    setIsExpanded(value => !value);
  }, []);

  return (
    <div className={clsx('flex flex-col items-center', className)}>
      <Headline i18nKey='landingPage:jobSearch.title' />
      <div className='text-secondary mb-6 flex gap-2 md:mb-8'>
        <p>
          <Trans components={{ span: <span className='text-main-blue' /> }} i18nKey='subtitle' t={t} />
        </p>
        <ArrowVertical className='text-main-blue mt-2 h-8 w-3 -scale-x-100 md:h-16 md:w-7' />
      </div>
      <div className='border-primary-200 mx-auto my-auto w-full rounded-2xl border-2 bg-white px-2 py-4 shadow-2xl md:px-12 md:py-8'>
        <PostalInputComponent
          dropdownHidden={Boolean(location)}
          onChange={event => {
            setAddressInput(event.target.value);
          }}
          onFocus={() => {
            if (location) {
              setAddressInput('');
              setLocation(null);
            }
          }}
          onSelected={handleAddressSelect}
          placeholder={t('addressPlaceholder')}
          selectedAddresses={location ? [location] : []}
          showIcon={false}
          variant={PostalInputVariant.Rounded}
          value={addressInput}
        />
      </div>
      {location && (
        <div className='mt-16 flex flex-col md:mt-28' ref={scrollRef}>
          <div className='mb-4 flex gap-2 self-center md:mb-8'>
            <ArrowVertical className='text-main-blue mt-2 h-8 w-3 md:h-16 md:w-7' />
            <p className='text-default text-center'>
              <Trans components={{ span: <span className='text-main-blue' /> }} i18nKey='nextStep' t={t} />
            </p>
          </div>
          {data && data.allJobTitles.length > 0 && (
            <div className='flex flex-col'>
              <div className='grid gap-4 md:grid-cols-3 md:gap-6'>
                {data.allJobTitles.slice(0, isExpanded ? undefined : COLLAPSED_LENGTH).map(jobTitle => {
                  const label = translateJobTitle(jobTitle);
                  const routeBasePath = registerOrigin === RegisterOrigin.PremiumLandingPage ? JOBS : JOB_BOARD;
                  const pathname = `${routeBasePath}/${kebabCase(label)}/${kebabCase(addressInput).toLowerCase()}`;

                  return (
                    <Link
                      href={registerOrigin ? { pathname, query: { registerOrigin } } : pathname}
                      key={jobTitle.id}
                      className='border-primary-200 text-default hover:bg-main-blue focus:bg-main-blue flex items-center justify-center rounded-2xl border bg-white p-2 text-center shadow-2xl outline-hidden transition-colors hover:text-white focus:text-white md:p-5 md:shadow-none'>
                      {label}
                    </Link>
                  );
                })}
              </div>
              <div className='mt-16 flex justify-center'>
                {data.allJobTitles.length > COLLAPSED_LENGTH && (
                  <button className={getButtonClassName('primary', 'w-48')} onClick={handleExpansionClick}>
                    {t(isExpanded ? 'less' : 'more')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobSearch;
